import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import gql from 'graphql-tag'
import { useQuery, useMutation } from '@apollo/react-hooks'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import CircularProgress from '@mui/material/CircularProgress'
import MenuIcon from '@mui/icons-material/Menu'
import AddIcon from '@mui/icons-material/Add'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { useAuthentication } from '../hooks/useAuthentication'
import {
  isAllowedOnBehalfOf,
  getAvailableDepartments,
  getDefaultFinancialYear,
  getDepartmentUser,
  findCurrentFinancialYear,
} from '../utils'
import {
  CREATE_VOUCHER,
  VoucherCreateInputMap,
  GET_USERS_LIST,
  GET_DEPARTMENTS,
  GET_FINANCIAL_YEARS,
} from '../components/Vouchers/gql'
import { User } from '../types'

export const IMPERSONATE_USER = gql`
  mutation impersonate($id: String!) {
    impersonate(id: $id) {
      token
      name
      user {
        first_name
        last_name
        email
        branch
        department {
          id
          name
        }
      }
    }
  }
`

type TopBarProps = {
  onMenuClick: () => void
}

const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const { authState, signOut, signIn } = useAuthentication()
  const history = useHistory()

  const [newVoucher, { loading, data }] = useMutation(CREATE_VOUCHER)
  const [impersonate, { loading: impersonateLoading }] =
    useMutation(IMPERSONATE_USER)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [notification, setNotification] = React.useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info' | 'warning'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const [deptDialogOpen, setDeptDialogOpen] = useState(false)
  const [selectedDeptId, setSelectedDeptId] = useState('')
  const [onBehalfOf, setOnBehalfOf] = useState(false)
  const [departments, setDepartments] = useState<Array<any>>([])
  const [financialYears, setFinancialYears] = useState<Array<any>>([])

  const departmentsResult = useQuery(GET_DEPARTMENTS)
  const financialYearsResult = useQuery(GET_FINANCIAL_YEARS)

  useEffect(() => {
    if (departmentsResult.data?.departments) {
      setDepartments(departmentsResult.data.departments)
    }
    if (financialYearsResult.data?.financialYears) {
      setFinancialYears(financialYearsResult.data.financialYears)
    }
  }, [departmentsResult.data, financialYearsResult.data])

  const allowedOnBehalfOf = isAllowedOnBehalfOf(
    authState.authUser.role_id,
    authState.authUser.email,
  )

  const usersResult = useQuery(GET_USERS_LIST)
  const [users, setUsers] = React.useState<Array<any>>([])

  useEffect(() => {
    if (usersResult.data?.users) {
      let users = usersResult.data.users
      if (authState.authUser.user_role?.code === 'SADR_BRANCH') {
        users = users.filter(
          (user: User) => user.branch === authState.authUser.branch,
        )
      }
      setUsers(users)
    }
  }, [
    usersResult.data,
    authState.authUser.user_role?.code,
    authState.authUser.branch,
  ])

  useEffect(() => {
    if (!loading && data?.createVoucher?.id) {
      history.push(`/panel/vouchers/${data.createVoucher.id}/edit`)
    }
  }, [loading, data, history])

  const handleImpersonate = async (userId: string) => {
    try {
      const result = await impersonate({
        variables: { id: userId },
        context: { headers: { authorization: authState.authToken } },
      })

      if (result.data?.impersonate?.token) {
        signIn(result.data.impersonate.token)
        handleProfileMenuClose()
        setNotification({
          open: true,
          message:
            'You are now impersonating ' +
            result.data.impersonate.user.first_name +
            ' ' +
            result.data.impersonate.user.last_name,
          severity: 'info',
        })
        setTimeout(() => {
          history.push('/panel')
          window.location.reload()
        }, 1500)
      }
    } catch (error) {
      console.error('Impersonation failed:', error)
      setNotification({
        open: true,
        message: 'Impersonation failed. Please try again.',
        severity: 'error',
      })
    }
  }

  const Impersonate = () => {
    return (
      <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="impersonate-select-label">
          {impersonateLoading ? 'Impersonating...' : 'Impersonate User'}
        </InputLabel>
        <Select
          labelId="impersonate-select-label"
          id="impersonate-select"
          label={impersonateLoading ? 'Impersonating...' : 'Impersonate User'}
          disabled={impersonateLoading}
          onChange={(event) => {
            const userId = event.target.value as string
            if (userId) {
              handleImpersonate(userId)
            }
          }}
          value=""
          sx={{
            '& .MuiSelect-select': {
              padding: '8px 12px',
              fontSize: '0.875rem',
            },
          }}
          endAdornment={
            impersonateLoading ? (
              <CircularProgress size={16} sx={{ marginRight: 1 }} />
            ) : null
          }
        >
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.first_name} {user.last_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  const isMenuOpen = Boolean(anchorEl)
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }))
  }

  const handleSaveAsDraft = (
    departmentOverride?: string,
    payToOverride?: string,
  ) => {
    if (loading) {
      return
    }
    const voucher = {
      description: '',
      department: departmentOverride || authState.authUser.department_id,
      total_amount: 0,
      receivers_account_number: '',
      financial_year:
        findCurrentFinancialYear(financialYears) || getDefaultFinancialYear(),
      created_by: authState.authUser.id,
      pay_to:
        payToOverride ||
        authState.authUser.first_name + ' ' + authState.authUser.last_name,
      claimed_by: authState.authUser.id,
      voucher_cost_category: [] as any,
    }
    const voucherInput = VoucherCreateInputMap(voucher)
    newVoucher({
      variables: { input: voucherInput },
    })
  }

  const handleCreateVoucherClick = () => {
    if (allowedOnBehalfOf) {
      setSelectedDeptId('')
      setOnBehalfOf(false)
      setDeptDialogOpen(true)
    } else {
      handleSaveAsDraft()
    }
  }

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 56, gap: 1 }}>
          <IconButton
            onClick={onMenuClick}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            aria-label="open navigation"
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            onClick={handleCreateVoucherClick}
            disabled={loading}
          >
            Create Voucher
          </Button>

          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-controls="user-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
          >
            <AccountCircleIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        id="user-menu"
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isMenuOpen}
        onClose={handleProfileMenuClose}
      >
        <MenuItem>
          {authState.authUser.first_name} {authState.authUser.last_name}
        </MenuItem>
        {authState.authUser.user_role?.code === 'SADR_BRANCH' &&
          authState.authUser.branch === 'STOCKHOLM' && (
            <MenuItem>
              <Impersonate />
            </MenuItem>
          )}
        <MenuItem onClick={signOut}>Log out</MenuItem>
      </Menu>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deptDialogOpen}
        onClose={() => setDeptDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Voucher</DialogTitle>
        <DialogContent>
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Checkbox
                checked={onBehalfOf}
                onChange={(e) => {
                  setOnBehalfOf(e.target.checked)
                  if (!e.target.checked) setSelectedDeptId('')
                }}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Create on behalf of another department
              </Typography>
            }
          />
          {onBehalfOf && (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="create-voucher-dept-label">Department</InputLabel>
              <Select
                labelId="create-voucher-dept-label"
                id="create-voucher-dept-select"
                value={selectedDeptId}
                label="Department"
                onChange={(e) => setSelectedDeptId(e.target.value as string)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                {getAvailableDepartments(departments, authState.authUser).map(
                  (d: any) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeptDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={(onBehalfOf && !selectedDeptId) || loading}
            onClick={() => {
              setDeptDialogOpen(false)
              const payTo = onBehalfOf
                ? getDepartmentUser(
                    users,
                    selectedDeptId,
                    authState.authUser.branch,
                  )
                : undefined
              const department = onBehalfOf ? selectedDeptId : undefined
              handleSaveAsDraft(department, payTo)
            }}
          >
            {loading ? <CircularProgress size={18} /> : 'Create Voucher'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TopBar
